import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useTranslation, useLanguage } from './LanguageContext';
import axios from 'axios';
import { API_URL } from '../config/api';

const UnitsContext = createContext();

// Conversion constants
const KG_TO_LBS = 2.20462;
const KM_TO_MI = 0.621371;
const CM_TO_INCH = 0.393701;
const KCAL_TO_KJ = 4.184;

// Default settings based on country code (more accurate than language)
const getDefaultSettingsByCountry = (countryCode) => {
  // Imperial countries (US, UK, Liberia, Myanmar)
  if (['US', 'LR', 'MM'].includes(countryCode)) {
    return {
      weight_unit: 'lbs',
      height_unit: 'ft',
      distance_unit: 'mi',
      date_format: 'MM/DD/YYYY',
      time_format: '12h',
      first_day_of_week: 'sunday',
      decimal_separator: 'period',
      energy_unit: 'kcal'
    };
  }
  // UK uses mix (imperial for height/weight, metric for distance)
  if (countryCode === 'GB') {
    return {
      weight_unit: 'lbs',
      height_unit: 'ft',
      distance_unit: 'mi',
      date_format: 'DD/MM/YYYY',
      time_format: '24h',
      first_day_of_week: 'monday',
      decimal_separator: 'period',
      energy_unit: 'kcal'
    };
  }
  // Metric for everyone else
  return {
    weight_unit: 'kg',
    height_unit: 'cm',
    distance_unit: 'km',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    first_day_of_week: 'monday',
    decimal_separator: 'comma',
    energy_unit: 'kcal'
  };
};

// Fallback: Default settings based on language
const getDefaultSettings = (lang) => {
  if (lang === 'en') {
    return {
      weight_unit: 'lbs',
      height_unit: 'ft',
      distance_unit: 'mi',
      date_format: 'MM/DD/YYYY',
      time_format: '12h',
      first_day_of_week: 'sunday',
      decimal_separator: 'period',
      energy_unit: 'kcal'
    };
  }
  // Metric for all other languages
  return {
    weight_unit: 'kg',
    height_unit: 'cm',
    distance_unit: 'km',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    first_day_of_week: 'monday',
    decimal_separator: 'comma',
    energy_unit: 'kcal'
  };
};

export function UnitsProvider({ children }) {
  const { user } = useAuth();
  const { language } = useTranslation();
  const { geoData } = useLanguage();
  
  // Initialize with defaults based on geo or language
  const [settings, setSettings] = useState(() => {
    // Check if we have cached geo data
    try {
      const cachedGeo = sessionStorage.getItem('app_geo_data');
      if (cachedGeo) {
        const geo = JSON.parse(cachedGeo);
        if (geo.countryCode) {
          return getDefaultSettingsByCountry(geo.countryCode);
        }
      }
    } catch (e) {}
    return getDefaultSettings(language);
  });
  
  const [userTimezone, setUserTimezone] = useState(() => {
    // Try to detect timezone from browser
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'Europe/Rome';
    }
  });

  // Listen for geo-detection events
  useEffect(() => {
    const handleGeoDetected = (event) => {
      const { countryCode } = event.detail;
      // Only update if user hasn't set preferences
      if (!user?.weight_unit) {
        console.log(`[Units] Geo detected: ${countryCode}, updating defaults`);
        setSettings(getDefaultSettingsByCountry(countryCode));
      }
    };

    window.addEventListener('geo-detected', handleGeoDetected);
    return () => window.removeEventListener('geo-detected', handleGeoDetected);
  }, [user?.weight_unit]);

  // Update from geoData if available and user hasn't set preferences
  useEffect(() => {
    if (geoData?.countryCode && !user?.weight_unit) {
      setSettings(getDefaultSettingsByCountry(geoData.countryCode));
    }
  }, [geoData, user?.weight_unit]);

  // Load user settings when user changes
  useEffect(() => {
    if (user) {
      // Use user's saved preferences if available
      setSettings(prev => ({
        ...prev,
        weight_unit: user.weight_unit || prev.weight_unit,
        height_unit: user.height_unit || prev.height_unit,
        distance_unit: user.distance_unit || prev.distance_unit,
        date_format: user.date_format || prev.date_format,
        time_format: user.time_format || prev.time_format,
        first_day_of_week: user.first_day_of_week || prev.first_day_of_week,
        decimal_separator: user.decimal_separator || prev.decimal_separator,
        energy_unit: user.energy_unit || prev.energy_unit
      }));
      if (user.user_timezone) {
        setUserTimezone(user.user_timezone);
      }
    }
  }, [user]);

  // Update defaults when language changes (only if user hasn't set preferences AND no geo data)
  useEffect(() => {
    if (!user?.weight_unit && !geoData?.countryCode) {
      setSettings(getDefaultSettings(language));
    }
  }, [language, user?.weight_unit, geoData?.countryCode]);

  // Save settings to backend
  const updateSettings = useCallback(async (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    
    if (user) {
      try {
        await axios.put(`${API_URL}/api/users/me`, newSettings, { withCredentials: true });
      } catch (err) {
        console.error('Failed to save unit settings:', err);
      }
    }
  }, [user]);

  // ═══════════════════════════════════════════════════════════════
  // CONVERSION FUNCTIONS
  // ═══════════════════════════════════════════════════════════════

  // Weight conversions
  const convertWeight = useCallback((valueInKg, toUnit = null) => {
    const unit = toUnit || settings.weight_unit;
    if (unit === 'lbs') {
      return valueInKg * KG_TO_LBS;
    }
    return valueInKg;
  }, [settings.weight_unit]);

  const convertWeightToKg = useCallback((value, fromUnit = null) => {
    const unit = fromUnit || settings.weight_unit;
    if (unit === 'lbs') {
      return value / KG_TO_LBS;
    }
    return value;
  }, [settings.weight_unit]);

  const formatWeight = useCallback((valueInKg, decimals = 1) => {
    const converted = convertWeight(valueInKg);
    const formatted = formatNumber(converted, decimals);
    return `${formatted} ${settings.weight_unit}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertWeight, settings.weight_unit]);

  // Distance conversions
  const convertDistance = useCallback((valueInKm, toUnit = null) => {
    const unit = toUnit || settings.distance_unit;
    if (unit === 'mi') {
      return valueInKm * KM_TO_MI;
    }
    return valueInKm;
  }, [settings.distance_unit]);

  const convertDistanceToKm = useCallback((value, fromUnit = null) => {
    const unit = fromUnit || settings.distance_unit;
    if (unit === 'mi') {
      return value / KM_TO_MI;
    }
    return value;
  }, [settings.distance_unit]);

  const formatDistance = useCallback((valueInKm, decimals = 2) => {
    const converted = convertDistance(valueInKm);
    const formatted = formatNumber(converted, decimals);
    const unit = settings.distance_unit === 'mi' ? 'mi' : 'km';
    return `${formatted} ${unit}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertDistance, settings.distance_unit]);

  // Height conversions
  const convertHeight = useCallback((valueInCm, toUnit = null) => {
    const unit = toUnit || settings.height_unit;
    if (unit === 'ft') {
      const totalInches = valueInCm * CM_TO_INCH;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return { feet, inches, totalInches };
    }
    return { cm: valueInCm };
  }, [settings.height_unit]);

  const convertHeightToCm = useCallback((value, fromUnit = null) => {
    const unit = fromUnit || settings.height_unit;
    if (unit === 'ft') {
      // Expect value as { feet, inches } or just total inches
      if (typeof value === 'object') {
        return (value.feet * 12 + value.inches) / CM_TO_INCH;
      }
      return value / CM_TO_INCH;
    }
    return value;
  }, [settings.height_unit]);

  const formatHeight = useCallback((valueInCm) => {
    if (settings.height_unit === 'ft') {
      const { feet, inches } = convertHeight(valueInCm);
      return `${feet}'${inches}"`;
    }
    return `${valueInCm} cm`;
  }, [convertHeight, settings.height_unit]);

  // Energy conversions
  const convertEnergy = useCallback((valueInKcal, toUnit = null) => {
    const unit = toUnit || settings.energy_unit;
    if (unit === 'kj') {
      return valueInKcal * KCAL_TO_KJ;
    }
    return valueInKcal;
  }, [settings.energy_unit]);

  const formatEnergy = useCallback((valueInKcal, decimals = 0) => {
    const converted = convertEnergy(valueInKcal);
    const formatted = formatNumber(converted, decimals);
    return `${formatted} ${settings.energy_unit}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertEnergy, settings.energy_unit]);

  // ═══════════════════════════════════════════════════════════════
  // NUMBER FORMATTING
  // ═══════════════════════════════════════════════════════════════

  const formatNumber = useCallback((value, decimals = 1) => {
    const num = Number(value).toFixed(decimals);
    if (settings.decimal_separator === 'comma') {
      return num.replace('.', ',');
    }
    return num;
  }, [settings.decimal_separator]);

  const parseNumber = useCallback((str) => {
    if (settings.decimal_separator === 'comma') {
      return parseFloat(str.replace(',', '.'));
    }
    return parseFloat(str);
  }, [settings.decimal_separator]);

  // ═══════════════════════════════════════════════════════════════
  // DATE/TIME FORMATTING
  // ═══════════════════════════════════════════════════════════════

  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    if (settings.date_format === 'MM/DD/YYYY') {
      return `${month}/${day}/${year}`;
    }
    return `${day}/${month}/${year}`;
  }, [settings.date_format]);

  const formatTime = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    if (settings.time_format === '12h') {
      const h = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      return `${h}:${minutes} ${ampm}`;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }, [settings.time_format]);

  const formatDateTime = useCallback((dateStr) => {
    return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
  }, [formatDate, formatTime]);

  // Get locale string for Intl APIs
  const getLocale = useCallback(() => {
    const localeMap = {
      it: 'it-IT',
      en: 'en-US',
      es: 'es-ES',
      pt: 'pt-BR',
      de: 'de-DE',
      fr: 'fr-FR'
    };
    return localeMap[language] || 'it-IT';
  }, [language]);

  const value = {
    settings,
    userTimezone,
    setUserTimezone,
    updateSettings,
    // Conversions
    convertWeight,
    convertWeightToKg,
    formatWeight,
    convertDistance,
    convertDistanceToKm,
    formatDistance,
    convertHeight,
    convertHeightToCm,
    formatHeight,
    convertEnergy,
    formatEnergy,
    // Formatting
    formatNumber,
    parseNumber,
    formatDate,
    formatTime,
    formatDateTime,
    getLocale,
    // Constants for UI
    weightUnit: settings.weight_unit,
    heightUnit: settings.height_unit,
    distanceUnit: settings.distance_unit,
    energyUnit: settings.energy_unit
  };

  return (
    <UnitsContext.Provider value={value}>
      {children}
    </UnitsContext.Provider>
  );
}

export function useUnits() {
  const context = useContext(UnitsContext);
  if (!context) {
    throw new Error('useUnits must be used within a UnitsProvider');
  }
  return context;
}

export default UnitsContext;
